"use client";

import { useMachine } from "@xstate/react";
import { useQueryState } from "nuqs";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { SnapshotFrom, StateValue } from "xstate";
import { enableKioskAnalytics } from "../devtool/kiosk-analytics";
import { traceInspector } from "../devtool/trace-inspector";
import { buildKioskMachineConfig } from "../lib/build-machine-config";
import {
  type EhrLocationIdSource,
  type KioskConfigOutcome,
  type PracticeIdSource,
  classifyKioskConfigState,
  outcomeForKioskConfigState,
} from "../lib/kiosk-config-state";
import { setKioskName } from "../lib/kiosk-identity";
import {
  type LogKioskConfigStateInput,
  logKioskConfigState,
} from "../lib/log-kiosk-config-state";
import {
  type KioskMachineContext as StoredKioskParams,
  hasKioskUrlParams,
  resolveUrlAuthoritativeId,
  retrieveAllParams,
  storeAllParams,
} from "../lib/param-storage";
import { useDerivedEhrLocationId } from "../lib/use-derived-ehr-location-id";
import { buildDevtoolJumpSnapshot } from "../machine/devtool-snapshot";
import { ARC_PARENT_STATE_NAMES } from "../machine/helper/arc-helpers";
import { createMedriteCheckinMachine } from "../machine/medrite-checkin.machine";
import { deserializeStateHybrid } from "../machine/state-serializer";
import type { KioskMachineEvent } from "../machine/types";
// Content block type (CMS content structure)
export interface ContentBlock {
  [key: string]: string | number | string[] | ContentBlock;
}

// Bundle type (what we fetch from the API)
export interface KioskBundle {
  content: Record<string, ContentBlock>;
  // Future: appointment, practice, preloadedData
}

type KioskMachine = ReturnType<typeof createMedriteCheckinMachine>;
export type KioskSnapshot = SnapshotFrom<KioskMachine>;

type SerializedState = ReturnType<typeof deserializeStateHybrid>;

interface KioskContextValue {
  // Query params
  practiceId: string | undefined | null;
  appointmentId: string | undefined | null;
  qrMode: boolean;
  pendingPricesMode: boolean;
  ehrLocationId: string | undefined | null;

  // Fetched data
  bundle: KioskBundle | null;
  isLoading: boolean;
  error: Error | null;

  // XState machine (NEW)
  machine: KioskMachine;
  state: KioskSnapshot;
  send: (event: KioskMachineEvent) => void;

  // Machine reset function
  resetMachine: () => void;

  // Health check snapshot for the devtool's Health tab + manual re-trigger.
  configHealthCheck: Omit<LogKioskConfigStateInput, "configState"> & {
    configState: ReturnType<typeof classifyKioskConfigState>;
    configOutcome: KioskConfigOutcome;
  };
  rerunHealthCheck: () => void;
}

/**
 * How long the practice fetch may stay pending before we report the kiosk as
 * stuck on the loading screen. Long enough to clear normal latency and React
 * Query's retries, short enough to surface within a rollout window.
 */
const STUCK_LOADING_REPORT_MS = 10_000;

const KioskContext = createContext<KioskContextValue | undefined>(undefined);

interface KioskProviderProps {
  children: React.ReactNode;
  contentBundle: Record<string, ContentBlock>;
}

// Shared loading UI while the kiosk resolves its launch config (practice fetch).
function KioskConfigLoading() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4" />
        <p className="text-gray-600">Loading kiosk...</p>
      </div>
    </div>
  );
}

// Gate component: resolves launch params and derives the ehrLocationId from the
// practiceId BEFORE the machine is built. The kiosk launch URL carries only
// practiceId; the ehrLocationId is looked up from the practice record and is the
// authoritative source (a same-practice cached value is only a last-resort
// fallback when derivation is unavailable). Machine construction is gated on
// that fetch so the kiosk shows the loading state instead of flashing the
// notConfigured/front-desk screen while the practice is still loading.
function KioskMachineProviderInner({
  children,
  contentBundle,
}: KioskProviderProps) {
  // 1. Parse query params using nuqs (hybrid: stateId or state)
  const [practiceIdParam, setPracticeIdParam] = useQueryState("practiceId");
  const [appointmentIdParam, setAppointmentIdParam] =
    useQueryState("appointmentId");
  const [qrModeParam, setQrModeParam] = useQueryState("qrMode");
  const [stateIdParam] = useQueryState("stateId"); // NEW: SessionStorage ID
  const [ehrLocationIdParam, setEhrLocationIdParam] =
    useQueryState("ehrLocationId");
  const [terminalIdParam, setTerminalIdParam] = useQueryState("terminalId"); // For tests: Stripe reader ID
  const [kioskNameParam, setKioskNameParam] = useQueryState("kioskName"); // Device name for observability

  // 2. Store params in localStorage if they exist in URL
  useEffect(() => {
    if (typeof window === "undefined") return;

    const hasUrlParams = hasKioskUrlParams({
      practiceId: practiceIdParam,
      appointmentId: appointmentIdParam,
      qrMode: qrModeParam,
      ehrLocationId: ehrLocationIdParam,
      terminalId: terminalIdParam,
      kioskName: kioskNameParam,
    });

    if (hasUrlParams) {
      // Store URL params to localStorage.
      //
      // ehrLocationId is intentionally NOT persisted: it is derived from the
      // resolved practiceId, never an independent launch param. Caching a
      // URL-supplied location would let a corrective `?ehrLocationId=` (or a
      // `?practiceId=P&ehrLocationId=L` launch with a foreign L) persist a
      // mixed {practiceId, foreignEhr} pair that the same-practice cache
      // fallback could later trust — greptile issues 1 & 2. We still let the URL
      // value be dissolved below so a legacy corrective param does not linger.
      storeAllParams({
        practiceId: practiceIdParam || undefined,
        appointmentId: appointmentIdParam || undefined,
        qrMode:
          qrModeParam === "true"
            ? true
            : qrModeParam === "false"
              ? false
              : undefined,
      });

      // Store terminalId separately (for Stripe reader)
      if (terminalIdParam) {
        localStorage.setItem("kiosk_stripe_reader_id", terminalIdParam);
      }

      // Store kioskName for device identification (defaults to "Medrite Kiosk")
      setKioskName(kioskNameParam || "Medrite Kiosk");

      // Dissolve URL params (remove them from URL)
      if (practiceIdParam) setPracticeIdParam(null);
      if (appointmentIdParam) setAppointmentIdParam(null);
      if (qrModeParam) setQrModeParam(null);
      if (ehrLocationIdParam) setEhrLocationIdParam(null);
      if (terminalIdParam) setTerminalIdParam(null);
      if (kioskNameParam) setKioskNameParam(null);
    }
  }, [
    practiceIdParam,
    appointmentIdParam,
    qrModeParam,
    terminalIdParam,
    kioskNameParam,
    setPracticeIdParam,
    setAppointmentIdParam,
    setQrModeParam,
    setEhrLocationIdParam,
    setTerminalIdParam,
    setKioskNameParam,
    ehrLocationIdParam,
  ]);

  // 3. Retrieve params from localStorage (these override context)
  const storedParams = useMemo(() => {
    if (typeof window === "undefined") return {};
    return retrieveAllParams();
  }, []);

  // Capture the URL-supplied practiceId once, at first render. The effect above
  // dissolves it from the URL right after mount, so a later re-render would
  // otherwise see `null` and fall back to a (possibly stale) cached value —
  // which could rebuild the machine mid-session with the wrong practice.
  const initialUrlPracticeIdRef = useRef(practiceIdParam);

  // The URL is authoritative for practiceId: on a fresh launch the start URL
  // carries the correctly-provisioned id, and a stale cached value must never
  // override it (that precedence bug misrouted check-ins to the wrong Experity
  // PM board). Fall back to the cache only when the URL does not carry the id
  // (in-session navigation). The dissolve effect above also rewrites the cache
  // from the URL, self-healing a stuck tablet on next launch.
  const practiceId = resolveUrlAuthoritativeId(
    initialUrlPracticeIdRef.current,
    storedParams.practiceId,
  );
  const appointmentId =
    storedParams.appointmentId || appointmentIdParam || undefined;
  const qrMode = storedParams.qrMode ?? qrModeParam === "true";

  // Derive the ehrLocationId from the resolved practiceId (org 24 practice
  // lookup). Kiosks launch with ONLY practiceId; the location is looked up from
  // the practice record. This derivation is the single source of truth for the
  // ehrLocationId — it is guaranteed to belong to the resolved practice, so it
  // can never pair a practiceId with a foreign location.
  const {
    derivedEhrLocationId,
    isPending: isPracticePending,
    practiceFoundInOrg,
    practiceFetchFailed,
  } = useDerivedEhrLocationId(practiceId);

  // A cached ehrLocationId is only safe to reuse when it belongs to the resolved
  // practiceId. When a corrective `?practiceId=` changes the practice, the cached
  // location is the PREVIOUS practice's and must never be paired with the new
  // practiceId — that mismatch routed consumer / patient-record writes to the
  // wrong EHR location (the wrong-practice bug this PR fixes). Gate the cache on
  // the resolved practiceId so it can only serve as a same-practice fallback.
  const cachedEhrLocationIdForResolvedPractice =
    storedParams.practiceId != null && storedParams.practiceId === practiceId
      ? storedParams.ehrLocationId || undefined
      : undefined;

  // Precedence — ehrLocationId is ALWAYS consistent with the resolved (winning)
  // practiceId:
  //   1. the value DERIVED from the resolved practiceId — the source of truth
  //      whenever derivation is available. There is intentionally NO explicit
  //      `?ehrLocationId=` URL override: kiosks only ever pass practiceId, and a
  //      URL/cached location that could win over derivation is exactly what let
  //      a foreign location get paired with the practice (greptile issues 1 & 2).
  //   2. otherwise a cached ehrLocationId, but ONLY when it belongs to the
  //      resolved practice (last-resort resilience when derivation is
  //      unavailable — network error / null column). A cross-practice cached
  //      value is discarded by the gate above.
  //   3. otherwise undefined, so the isEhrLocationIdMissing fail-safe routes to
  //      notConfigured (and fires the Datadog log).
  const ehrLocationId =
    derivedEhrLocationId ?? cachedEhrLocationIdForResolvedPractice;

  // 4. Deserialize state with hybrid strategy (localStorage or direct)
  const serializedState = useMemo(() => {
    // Try stateId first (localStorage lookup)
    if (stateIdParam) {
      const deserialized = deserializeStateHybrid(stateIdParam);
      if (deserialized) {
        return deserialized;
      }
    }

    return null;
  }, [stateIdParam]);

  // Gate machine construction on the practice fetch. Because the derived value
  // is now the authoritative source, we ALWAYS wait for it whenever we have a
  // practiceId to derive from — even when a same-practice cached location exists.
  // Letting a cached location skip the gate is what allowed a stale/mixed cached
  // ehrLocationId to build the machine before derivation could override it
  // (greptile issue 1). The cached value is a fallback used only AFTER derivation
  // settles without a value, never a reason to skip the wait.
  // A genuine fetch error / missing practice ends the pending state, so the
  // machine builds with an undefined ehrLocationId and the isEhrLocationIdMissing
  // fail-safe routes to notConfigured (and fires the Datadog log) — as intended.
  const canDeriveFromPractice =
    typeof practiceId === "string" && practiceId.length > 0;
  const shouldWaitForPractice = canDeriveFromPractice && isPracticePending;

  // ── Rollout observability ────────────────────────────────────────────────
  // Emit exactly one launch-config event per load, covering healthy loads too,
  // so the fleet has a denominator during rollout. Must live above the early
  // return below — hooks cannot be declared after it.
  const practiceIdSource: PracticeIdSource = initialUrlPracticeIdRef.current
    ? "url"
    : storedParams.practiceId
      ? "cache"
      : "none";
  const ehrLocationIdSource: EhrLocationIdSource = derivedEhrLocationId
    ? "derived"
    : cachedEhrLocationIdForResolvedPractice
      ? "cache"
      : "none";

  // Single snapshot of the launch-config resolution, shared by the automatic
  // once-per-load Datadog log below and the on-device Health tab in
  // KioskDevTool — so on-site staff can see the same fields without leaving
  // the kiosk, and manually re-fire the event (tagged `triggerSource: "manual"`)
  // for correlation while troubleshooting.
  const configHealthCheckInput: Omit<LogKioskConfigStateInput, "configState"> =
    useMemo(
      () => ({
        practiceIdSource,
        ehrLocationIdSource,
        practiceId,
        ehrLocationId,
        practiceFoundInOrg,
        practiceFetchFailed,
        restoredFromStateId: Boolean(stateIdParam),
      }),
      [
        practiceIdSource,
        ehrLocationIdSource,
        practiceId,
        ehrLocationId,
        practiceFoundInOrg,
        practiceFetchFailed,
        stateIdParam,
      ],
    );

  const hasLoggedConfigStateRef = useRef(false);

  useEffect(() => {
    if (shouldWaitForPractice || hasLoggedConfigStateRef.current) return;
    hasLoggedConfigStateRef.current = true;

    logKioskConfigState({
      ...configHealthCheckInput,
      configState: classifyKioskConfigState(configHealthCheckInput),
    });
  }, [shouldWaitForPractice, configHealthCheckInput]);

  // A practice fetch that never settles leaves the kiosk on the loading screen
  // indefinitely with no signal at all. Report it once so "stuck" is visible as
  // its own bucket rather than as silence.
  useEffect(() => {
    if (!shouldWaitForPractice) return;
    const timer = setTimeout(() => {
      if (hasLoggedConfigStateRef.current) return;
      hasLoggedConfigStateRef.current = true;
      logKioskConfigState({
        configState: "stuck_loading",
        practiceIdSource,
        ehrLocationIdSource: "none",
        practiceId,
        ehrLocationId: undefined,
        practiceFoundInOrg: undefined,
        practiceFetchFailed: false,
        restoredFromStateId: Boolean(stateIdParam),
      });
    }, STUCK_LOADING_REPORT_MS);
    return () => clearTimeout(timer);
  }, [shouldWaitForPractice, practiceIdSource, practiceId, stateIdParam]);

  if (shouldWaitForPractice) {
    return <KioskConfigLoading />;
  }

  return (
    <KioskMachineHost
      contentBundle={contentBundle}
      qrMode={qrMode}
      practiceId={practiceId}
      appointmentId={appointmentId}
      ehrLocationId={ehrLocationId}
      terminalId={terminalIdParam}
      serializedState={serializedState}
      storedParams={storedParams}
      configHealthCheckInput={configHealthCheckInput}
    >
      {children}
    </KioskMachineHost>
  );
}

interface KioskMachineHostProps extends KioskProviderProps {
  qrMode: boolean;
  practiceId: string | undefined;
  appointmentId: string | undefined;
  ehrLocationId: string | undefined;
  terminalId: string | null;
  serializedState: SerializedState;
  storedParams: StoredKioskParams;
  configHealthCheckInput: Omit<LogKioskConfigStateInput, "configState">;
}

// Host component: builds and runs the XState machine with the already-resolved
// launch config (including the derived ehrLocationId) and exposes it via context.
// It is mounted by the gate only once the config is ready, so the machine is
// constructed exactly once with its final ehrLocationId (no build-with-undefined
// then rebuild).
function KioskMachineHost({
  children,
  contentBundle,
  qrMode,
  practiceId,
  appointmentId,
  ehrLocationId,
  terminalId,
  serializedState,
  storedParams,
  configHealthCheckInput,
}: KioskMachineHostProps) {
  // 5. Create machine instance with the resolved launch config (once per host)
  const machine = useMemo(() => {
    // Get stripeReaderId from URL param (for tests) or localStorage
    // URL param takes precedence since useEffect hasn't run yet on first render
    const savedReaderId =
      typeof window !== "undefined"
        ? terminalId || localStorage.getItem("kiosk_stripe_reader_id")
        : null;

    return createMedriteCheckinMachine(
      buildKioskMachineConfig({
        qrMode,
        practiceId,
        appointmentId,
        ehrLocationId,
        stripeReaderId: savedReaderId,
      }),
    );
  }, [qrMode, practiceId, appointmentId, ehrLocationId, terminalId]);

  // 6. Build initial snapshot for machine start.
  //
  // Two cases:
  // a) Arc jump (serializedState.arc present): build a full XState persisted
  //    snapshot with the child arc actor restored to its specific internal state.
  //    The arc field travels through the stateId URL param (same as non-arc jumps)
  //    so it is available during both SSR and client hydration.
  // b) Normal reload (no arc field): resolve a live parent-only snapshot via
  //    machine.resolveState(). The arc starts fresh when the parent enters the
  //    arc-invoking state.
  const initialSnapshot = useMemo(() => {
    if (!serializedState) return undefined;

    // Override serialized context with the resolved launch config (it takes
    // precedence over the serialized snapshot).
    const overriddenContext = {
      ...serializedState.context,
      // practiceId is URL-authoritative on restore too. Use the already-resolved
      // `practiceId` (URL wins, else cache) rather than the raw
      // `storedParams.practiceId`, which is the pre-effect cache and can still be
      // a DIFFERENT (stale) practice when a corrective `?practiceId=` launched
      // the restore. The resolved ehrLocationId below is derived from this same
      // winning practiceId, so pinning both to it keeps practiceId + ehrLocationId
      // from ONE practice instead of mixing a stale practiceId with the corrected
      // location in booking / patient-record writes.
      //
      // Assign unconditionally (not `...(practiceId && { practiceId })`): a
      // conditional spread only overwrites when the resolved value is truthy, so
      // a genuinely-missing practiceId/ehrLocationId (fail-safe case, e.g. the
      // practice lookup settles with no location on file) would leave the OLD
      // serialized value in place instead of clearing it — silently masking the
      // missing-config guard with stale, nonempty data.
      practiceId,
      ...(storedParams.appointmentId && {
        appointmentId: storedParams.appointmentId,
      }),
      ...(storedParams.qrMode !== undefined && { qrMode: storedParams.qrMode }),
      // ehrLocationId is practice-derived on restore too: the value looked up
      // from the resolved (winning) practiceId must override the stale/missing
      // serialized value, mirroring the fresh-machine path (see the resolved
      // `ehrLocationId` threaded into buildKioskMachineConfig above). Because it
      // is derived from the same `practiceId` pinned just above, the restored
      // snapshot cannot mix a practiceId with a foreign location. Without this, a
      // restored session keeps the persisted location or dead-ends at
      // `notConfigured`.
      ehrLocationId,
    };

    if (serializedState.arc) {
      // Arc jump: build a PersistedMachineSnapshot with the child arc actor
      // restored to the copied internal state.
      const parentValue = serializedState.value;
      if (typeof parentValue !== "string") {
        return undefined;
      }
      const snapshot = buildDevtoolJumpSnapshot({
        parentValue,
        parentContext: overriddenContext,
        arcId: serializedState.arc.arcId,
        arcStateName: serializedState.arc.stateName,
        arcContext: serializedState.arc.context,
      });
      return snapshot as unknown as KioskSnapshot;
    }

    // Safety: arc-invoking states without arc info can't be properly restored
    // (resolveState won't re-invoke the child actor, causing "Processing..." screen).
    // Only block known arc states — other componentName-less states (e.g.,
    // verifyingInsurance, fetchingDefaultPricing) should still go through
    // resolveState so their services re-invoke normally.
    // ARC_PARENT_STATE_NAMES is derived from ARC_STATES in arc-helpers.ts.
    const stateValue =
      typeof serializedState.value === "string"
        ? serializedState.value
        : Object.keys(serializedState.value as Record<string, unknown>)[0];
    if (ARC_PARENT_STATE_NAMES.has(stateValue)) {
      console.warn(
        `[kiosk-provider] Skipping restore to arc state without arc info: ${stateValue}`,
      );
      return undefined;
    }

    return machine.resolveState({
      value: serializedState.value as StateValue,
      context: overriddenContext,
    });
  }, [serializedState, machine, storedParams, practiceId, ehrLocationId]);

  // 7. Start the machine.
  const [state, rawSend] = useMachine(machine, {
    snapshot: initialSnapshot,
    inspect: traceInspector.inspect,
  });

  // 8b. Start trace when machine initializes
  const traceStartedRef = useRef(false);

  useEffect(() => {
    if (traceStartedRef.current) return;
    traceStartedRef.current = true;

    const stateName =
      typeof state.value === "string"
        ? state.value
        : Object.keys(state.value)[0] || "unknown";

    traceInspector.startTrace(
      "medrite-checkin",
      stateName,
      state.context as Record<string, unknown>,
    );

    return () => {
      traceInspector.endTrace();
    };
  }, [state.value, state.context]);

  // 7b. Enable automatic Mixpanel analytics for machine events
  useEffect(() => {
    return enableKioskAnalytics();
  }, []);

  // 7b. Throttle send to prevent rapid double-tap during React re-render gap
  // Track by state + event type combination to allow same event after state change
  const lastEventTimeRef = useRef<Record<string, number>>({});
  const THROTTLE_MS = 1000;

  const send = useCallback(
    (event: KioskMachineEvent) => {
      const now = Date.now();
      const eventType = event.type;
      const stateKey = JSON.stringify(state.value);
      const throttleKey = `${stateKey}:${eventType}`;
      const lastTime = lastEventTimeRef.current[throttleKey] || 0;

      // Allow if enough time has passed since last event of this type in this state
      if (now - lastTime > THROTTLE_MS) {
        lastEventTimeRef.current[throttleKey] = now;
        rawSend(event);
      }
    },
    [rawSend, state.value],
  );

  // Note: We no longer clear state params after hydration
  // The renderer will manage URL state persistence automatically on beforeunload

  // 8. Create bundle (for now, just combine what we have)
  const bundle: KioskBundle = useMemo(
    () => ({
      content: contentBundle,
    }),
    [contentBundle],
  );

  // resetMachine is handled by outer component via key prop
  // Expose a no-op here to satisfy context type
  const resetMachine = useCallback(() => {
    // This will be replaced by the outer component
  }, []);

  // Derived classification + manual re-trigger for the devtool Health tab.
  const configState = useMemo(
    () => classifyKioskConfigState(configHealthCheckInput),
    [configHealthCheckInput],
  );

  const configHealthCheck = useMemo(
    () => ({
      ...configHealthCheckInput,
      configState,
      configOutcome: outcomeForKioskConfigState(configState),
    }),
    [configHealthCheckInput, configState],
  );

  const rerunHealthCheck = useCallback(() => {
    logKioskConfigState({
      ...configHealthCheckInput,
      configState,
      triggerSource: "manual",
    });
  }, [configHealthCheckInput, configState]);

  const value: KioskContextValue = {
    practiceId,
    appointmentId,
    qrMode,
    pendingPricesMode: state.context.pendingPricesMode ?? false,
    ehrLocationId,
    bundle,
    isLoading: false,
    error: null,
    machine,
    state,
    send,
    resetMachine,
    configHealthCheck,
    rerunHealthCheck,
  };

  return (
    <KioskContext.Provider value={value}>{children}</KioskContext.Provider>
  );
}

// Outer component that manages the machine key
export function KioskProvider({ children, contentBundle }: KioskProviderProps) {
  const [machineKey, setMachineKey] = useState(0);

  // Wrap the reset function in context
  const resetMachine = useCallback(() => {
    setMachineKey((prev) => prev + 1);
  }, []);

  // Use a wrapper to inject resetMachine into context
  return (
    <KioskMachineProviderInner key={machineKey} contentBundle={contentBundle}>
      <KioskResetInjector resetMachine={resetMachine}>
        {children}
      </KioskResetInjector>
    </KioskMachineProviderInner>
  );
}

// Component to inject resetMachine into existing context
function KioskResetInjector({
  children,
  resetMachine,
}: {
  children: React.ReactNode;
  resetMachine: () => void;
}) {
  const context = useContext(KioskContext);
  if (!context) {
    throw new Error("KioskResetInjector must be used within KioskProvider");
  }

  // Replace the no-op resetMachine with the real one
  const enhancedValue: KioskContextValue = {
    ...context,
    resetMachine,
  };

  return (
    <KioskContext.Provider value={enhancedValue}>
      {children}
    </KioskContext.Provider>
  );
}

export function useKiosk() {
  const context = useContext(KioskContext);
  if (!context) {
    throw new Error("useKiosk must be used within KioskProvider");
  }
  return context;
}

export function useKioskSafe() {
  return useContext(KioskContext) ?? null;
}
